// @ts-check

/**
 * @file Renders one project board column card.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Animated, PanResponder, Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import ProjectBoardTaskCard, {PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM} from "./board-task-card"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {InstanceType<typeof import("../../../models/board-column.js").default>} BoardColumn */
/** @typedef {import("./local-board-data.js").LocalTaskBoardReadView} TaskBoard */
/** @type {Record<string, object>} */
const styles = {}
export const PROJECT_BOARD_COLUMN_CARD_MARGIN_RIGHT = 16
export const PROJECT_BOARD_COLUMN_CARD_WIDTH = 320
const DEFAULT_TASK_BOARD_SLOT_HEIGHT =
  52 + PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM

/** @typedef {(...args: unknown[]) => unknown} ProjectBoardColumnCardCallback */

/** @typedef {{height: number, width: number, x: number, y: number}} BoardItemLayout */

/** @typedef {{rowNumber: number, sourceColumnId: string, targetColumnId: string, taskBoardId: string} | null} ProjectBoardDragPreview */

/**
 * @typedef {object} ProjectBoardColumnCardProps
 * @property {Record<string, InstanceType<typeof import("../../../models/user.js").default>[]>} [assignedUsersByTaskId] Catalog users keyed by local task id.
 * @property {BoardColumn} column Column shown by the card.
 * @property {ProjectBoardDragPreview} dragPreview Current drag target used for live spacing.
 * @property {ProjectBoardColumnCardCallback} onAddTaskPress Add-task callback.
 * @property {ProjectBoardColumnCardCallback} onBoardColumnDragEnd Column drag-end callback.
 * @property {ProjectBoardColumnCardCallback} onBoardColumnDragMove Column drag-move callback.
 * @property {ProjectBoardColumnCardCallback} onBoardColumnDragStart Column drag-start callback.
 * @property {ProjectBoardColumnCardCallback} onBoardColumnMoveEnd Column move callback.
 * @property {ProjectBoardColumnCardCallback} onColumnLayoutChange Column layout callback.
 * @property {ProjectBoardColumnCardCallback} onColumnMenuPress Column menu callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardDragEnd Drag-end callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardDragMove Drag-move callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardDragStart Drag-start callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardLayoutChange Task-card layout callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardMenuPress Task menu callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardMoveEnd Task move callback.
 * @property {ProjectBoardColumnCardCallback} onTaskBoardPress Task card callback.
 * @property {number} orderIndex Current index in the rendered board row.
 * @property {number} previewOffsetX Live sibling-spacing offset.
 * @property {number} settleOffsetX Final slot offset for release animation.
 * @property {TaskBoard[]} taskBoards Task boards in the column.
 */

/**
 * @typedef {object} ProjectBoardColumnCardState
 * @property {string | null} draggingTaskBoardId Task board currently dragging from this column.
 * @property {boolean} isDragging Whether the column is currently dragging.
 * @property {boolean} isSettling Whether the column is animating into its final slot.
 * @property {number} layoutRevision Revision counter for measured task-card slot heights.
 */

/**
 * Project board column with task cards.
 * @augments {ShapeComponent<ProjectBoardColumnCardProps, ProjectBoardColumnCardState>}
 */
class ProjectBoardColumnCard extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  static propTypes = PropTypesExact({
    assignedUsersByTaskId: PropTypes.object,
    column: PropTypes.object.isRequired,
    dragPreview: PropTypes.object,
    onAddTaskPress: PropTypes.func.isRequired,
    onBoardColumnDragEnd: PropTypes.func.isRequired,
    onBoardColumnDragMove: PropTypes.func.isRequired,
    onBoardColumnDragStart: PropTypes.func.isRequired,
    onBoardColumnMoveEnd: PropTypes.func.isRequired,
    onColumnLayoutChange: PropTypes.func.isRequired,
    onColumnMenuPress: PropTypes.func.isRequired,
    onTaskBoardDragEnd: PropTypes.func.isRequired,
    onTaskBoardDragMove: PropTypes.func.isRequired,
    onTaskBoardDragStart: PropTypes.func.isRequired,
    onTaskBoardLayoutChange: PropTypes.func.isRequired,
    onTaskBoardMenuPress: PropTypes.func.isRequired,
    onTaskBoardMoveEnd: PropTypes.func.isRequired,
    onTaskBoardPress: PropTypes.func.isRequired,
    orderIndex: PropTypes.number.isRequired,
    previewOffsetX: PropTypes.number.isRequired,
    settleOffsetX: PropTypes.number.isRequired,
    taskBoards: PropTypes.array.isRequired
  })

  static defaultProps = {
    assignedUsersByTaskId: undefined
  }
  columnDragPosition = new Animated.ValueXY()
  columnDragTransformStyle = {
    transform: this.columnDragPosition.getTranslateTransform()
  }
  columnPreviewOffsetPosition = new Animated.Value(0)
  columnPreviewTransformStyle = {
    transform: [
      {
        translateX: this.columnPreviewOffsetPosition
      }
    ]
  }
  /** @type {BoardItemLayout | null} */
  columnLayout = null

  /** @type {BoardItemLayout | null} */
  contentLayout = null

  columnMovePanResponder = PanResponder.create({
    onMoveShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: () => {
      this.onColumnDragStart()
    },
    onPanResponderMove: Animated.event(
      [
        null,
        {
          dx: this.columnDragPosition.x,
          dy: this.columnDragPosition.y
        }
      ],
      {
        useNativeDriver: false,
        listener: (event, gestureState) => {
          this.onColumnDragMove(event, gestureState)
        }
      }
    ),
    onPanResponderRelease: (event, gestureState) => {
      this.onColumnDragRelease(event, gestureState)
    },
    onPanResponderTerminate: () => {
      this.onColumnDragTerminate()
    },
    onStartShouldSetPanResponder: () => true,
    onStartShouldSetPanResponderCapture: () => true
  })
  /** @type {Map<string, BoardItemLayout>} */
  taskBoardLayouts = new Map()

  /** @type {Map<string, number>} */
  taskBoardSlotHeights = new Map()

  /** @type {ProjectBoardColumnCardState} */
  state = {
    draggingTaskBoardId: null,
    isDragging: false,
    isSettling: false,
    layoutRevision: 0
  }

  /** Initializes locale handling. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /** Stops pending animations before the column unmounts. */
  componentWillUnmount() {
    this.columnDragPosition.stopAnimation()
    this.columnPreviewOffsetPosition.stopAnimation()
  }

  /**
   * Renders the board column.
   * @returns {ReactNode} Board column content.
   */
  render() {
    /** @type {BoardColumn} */
    const column = this.p.column
    const columnId = column.id()
    /** @type {TaskBoard[]} */
    const taskBoards = this.p.taskBoards
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const taskDragging = this.s.draggingTaskBoardId !== null
    const columnMoving = this.s.isDragging || this.s.isSettling
    const layoutRevision = this.s.layoutRevision
    void layoutRevision
    return (
      <Animated.View
        onLayout={this.tt.onColumnLayout}
        style={[
          (styles[`card-${themeName}-${taskDragging}-${columnMoving}`] ||= {
            backgroundColor: colors.surfaceMuted,
            borderColor: colors.border,
            borderRadius: 18,
            borderWidth: 1,
            marginRight: PROJECT_BOARD_COLUMN_CARD_MARGIN_RIGHT,
            overflow: "visible",
            padding: 16,
            position: "relative",
            width: PROJECT_BOARD_COLUMN_CARD_WIDTH,
            zIndex: columnMoving ? 30 : taskDragging ? 20 : 1
          }),
          columnMoving ? this.columnDragTransformStyle : this.columnPreviewTransformStyle
        ]}
        testID={`projectBoardScreen/column/${columnId}`}
      >
        <View
          style={styles[`cardSurface-${themeName}`] ||= {
            backgroundColor: colors.surfaceMuted,
            overflow: "visible"
          }}
          testID={`projectBoardScreen/column/${columnId}/surface`}
        >
          <View
            style={styles.header ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 12
            }}
            testID={`projectBoardScreen/column/${columnId}/header`}
          >
            <View
              style={styles.headerInfo ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8
              }}
              testID={`projectBoardScreen/column/${columnId}/headerInfo`}
            >
              <Text
                style={styles[`headerTitle-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase"
                }}
                testID={`projectBoardScreen/column/${columnId}/title`}
              >
                {column.name() || _("Untitled column")}
              </Text>
              <Text
                style={styles[`headerCount-${themeName}`] ||= {
                  backgroundColor: colors.chipActiveBackground,
                  borderRadius: 999,
                  color: colors.chipActiveText,
                  fontSize: 12,
                  fontWeight: 700,
                  paddingHorizontal: 8,
                  paddingVertical: 4
                }}
                testID={`projectBoardScreen/column/${columnId}/count`}
              >
                {taskBoards.length}
              </Text>
            </View>
            <View
              style={styles.headerActions ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8
              }}
              testID={`projectBoardScreen/column/${columnId}/headerActions`}
            >
              <Pressable
                accessibilityLabel={_("Add task")}
                onPress={this.tt.onAddTaskPress}
                style={styles[`addTaskButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.infoBackground,
                  borderRadius: 999,
                  justifyContent: "center",
                  minHeight: 32,
                  minWidth: 32
                }}
                testID={`projectBoardScreen/column/${columnId}/addTaskButton`}
              >
                <View
                  style={styles.addTaskButtonIcon ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID={`projectBoardScreen/column/${columnId}/addTaskButton/icon`}
                >
                  <FontAwesome6 color={colors.link} name="plus" size={14} />
                </View>
              </Pressable>
              <Pressable
                accessibilityLabel={_("More")}
                onPress={this.tt.onColumnMenuPress}
                style={styles[`menuButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.chipBackground,
                  borderRadius: 999,
                  justifyContent: "center",
                  minHeight: 32,
                  minWidth: 32
                }}
                testID={`projectBoardScreen/column/${columnId}/menuButton`}
              >
                <View
                  style={styles.menuButtonIcon ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID={`projectBoardScreen/column/${columnId}/menuButton/icon`}
                >
                  <FontAwesome6 color={colors.chipText} name="ellipsis-vertical" size={14} />
                </View>
              </Pressable>
              <Animated.View
                {...this.columnMovePanResponder.panHandlers}
                accessibilityLabel={_("Move")}
                style={styles[`moveHandle-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.infoBackground,
                  borderRadius: 999,
                  justifyContent: "center",
                  minHeight: 32,
                  minWidth: 32
                }}
                testID={`projectBoardScreen/column/${columnId}/moveHandle`}
              >
                <View
                  style={styles.moveHandleIcon ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID={`projectBoardScreen/column/${columnId}/moveHandle/icon`}
                >
                  <FontAwesome6 color={colors.link} name="arrows-up-down-left-right" size={14} />
                </View>
              </Animated.View>
            </View>
          </View>
          <View
            onLayout={this.tt.onContentLayout}
            style={styles.content ||= {
              minHeight: 84
            }}
            testID={`projectBoardScreen/column/${columnId}/content`}
          >
            {taskBoards.length === 0 &&
              <Text
                style={styles[`empty-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  paddingVertical: 8
                }}
                testID={`projectBoardScreen/column/${columnId}/empty`}
              >
                {_("No tasks in this column")}
              </Text>
            }
            {taskBoards.map((taskBoard, taskBoardIndex) => (
              <ProjectBoardTaskCard
                assignedUsers={this.p.assignedUsersByTaskId?.[String(taskBoard.taskId())] || []}
                key={taskBoard.id()}
                onTaskBoardDragEnd={this.tt.onTaskBoardDragEnd}
                onTaskBoardDragMove={this.p.onTaskBoardDragMove}
                onTaskBoardDragStart={this.tt.onTaskBoardDragStart}
                onTaskBoardLayout={this.tt.onTaskBoardLayout}
                onTaskBoardMenuPress={this.p.onTaskBoardMenuPress}
                onTaskBoardMoveEnd={this.p.onTaskBoardMoveEnd}
                onTaskBoardPress={this.p.onTaskBoardPress}
                orderIndex={taskBoardIndex}
                previewOffsetY={this.previewOffsetYForTaskBoard(taskBoard)}
                settleOffsetForTaskBoard={this.tt.settleOffsetYForTaskBoard}
                taskBoard={taskBoard}
              />
            ))}
          </View>
        </View>
      </Animated.View>
    )
  }

  /**
   * Re-reports task layouts when React reorders existing task-card nodes.
   * @param {ProjectBoardColumnCardProps} previousProps Props from the previous render.
   * @returns {void} Nothing.
   */
  componentDidUpdate(previousProps) {
    if (previousProps.orderIndex !== this.p.orderIndex) {
      this.columnPreviewOffsetPosition.setValue(this.p.previewOffsetX)
    } else if (
      previousProps.previewOffsetX !== this.p.previewOffsetX &&
      !this.s.isDragging
    ) {
      if (this.s.isSettling) {
        this.stopColumnSettleForPreview(this.p.previewOffsetX)
      } else {
        this.animateColumnPreviewOffset(this.p.previewOffsetX)
      }
    }
    if (!taskBoardIdsMatch(previousProps.taskBoards, this.p.taskBoards)) {
      this.reportTaskBoardLayouts()
    }
  }

  /**
   * Stores this column's layout in board-row coordinates.
   * @param {{nativeEvent?: {layout?: BoardItemLayout}}} event React Native layout payload.
   * @returns {void} Nothing.
   */
  onColumnLayout = (event) => {
    const layout = layoutFromEvent(event)
    if (!layout) {
      return
    }
    this.columnLayout = layout
    this.p.onColumnLayoutChange(this.p.column, layout)
    this.reportTaskBoardLayouts()
  }

  /**
   * Stores the task-list content layout inside this column.
   * @param {{nativeEvent?: {layout?: BoardItemLayout}}} event React Native layout payload.
   * @returns {void} Nothing.
   */
  onContentLayout = (event) => {
    const layout = layoutFromEvent(event)
    if (!layout) {
      return
    }
    this.contentLayout = layout
    this.reportTaskBoardLayouts()
  }

  /** Starts adding a task to this column. */
  onAddTaskPress = () => {
    this.p.onAddTaskPress(this.p.column)
  }

  /** Opens the menu for this column. */
  onColumnMenuPress = () => {
    this.p.onColumnMenuPress(this.p.column)
  }

  /** Starts column dragging. */
  onColumnDragStart = () => {
    this.columnPreviewOffsetPosition.setValue(0)
    this.columnDragPosition.setOffset({
      x: 0,
      y: 0
    })
    this.columnDragPosition.setValue({
      x: 0,
      y: 0
    })
    this.s.isDragging = true
    this.p.onBoardColumnDragStart(this.p.column)
  }

  /**
   * Handles drag movement for live board-column spacing.
   * @param {object} event Pan responder event.
   * @param {{moveX?: number, moveY?: number}} gestureState Coordinates used to update the target slot.
   * @returns {void} Nothing.
   */
  onColumnDragMove = (event, gestureState) => {
    this.p.onBoardColumnDragMove(this.p.column, event, gestureState)
  }

  /**
   * Finishes a column drag gesture.
   * @param {object} event Pan responder event.
   * @param {{dx: number, dy: number}} gestureState Gesture delta state.
   * @returns {void} Nothing.
   */
  onColumnDragRelease = (event, gestureState) => {
    const settleOffsetX = this.p.settleOffsetX
    this.columnDragPosition.flattenOffset()
    this.s.isDragging = false
    this.s.isSettling = true
    void this.p.onBoardColumnMoveEnd(this.p.column, event, gestureState)
    this.columnDragPosition.setValue({
      x: (gestureState.dx || 0) - settleOffsetX,
      y: gestureState.dy || 0
    })
    Animated.spring(this.columnDragPosition, {
      toValue: {
        x: 0,
        y: 0
      },
      useNativeDriver: false
    }).start(() => {
      this.s.isSettling = false
      this.p.onBoardColumnDragEnd(this.p.column)
    })
  }

  /** Cancels column dragging. */
  onColumnDragTerminate = () => {
    this.columnDragPosition.flattenOffset()
    this.s.isDragging = false
    this.s.isSettling = false
    this.p.onBoardColumnDragEnd(this.p.column)
    Animated.spring(this.columnDragPosition, {
      toValue: {
        x: 0,
        y: 0
      },
      useNativeDriver: false
    }).start()
  }

  /**
   * Animates this column into a live preview offset.
   * @param {number} previewOffsetX Target preview offset.
   * @returns {void} Nothing.
   */
  animateColumnPreviewOffset(previewOffsetX) {
    Animated.spring(this.columnPreviewOffsetPosition, {
      toValue: previewOffsetX,
      useNativeDriver: false
    }).start()
  }

  /**
   * Ends a release animation so this column can move as another column's sibling.
   * @param {number} previewOffsetX Target preview offset.
   * @returns {void} Nothing.
   */
  stopColumnSettleForPreview(previewOffsetX) {
    this.columnDragPosition.stopAnimation((currentValue) => {
      const currentX =
        typeof currentValue?.x === "number" ? currentValue.x : 0
      this.columnPreviewOffsetPosition.setValue(currentX)
      this.columnDragPosition.setValue({
        x: 0,
        y: 0
      })
      this.s.isSettling = false
      this.animateColumnPreviewOffset(previewOffsetX)
    })
  }

  /**
   * Lifts this column above sibling columns while one of its cards is dragged.
   * @param {TaskBoard} taskBoard Dragged task board.
   * @returns {void} Nothing.
   */
  onTaskBoardDragStart = (taskBoard) => {
    this.s.draggingTaskBoardId = taskBoard.id()
    this.p.onTaskBoardDragStart(taskBoard)
  }

  /**
   * Releases this column back into normal stacking after dragging finishes.
   * @param {TaskBoard} taskBoard Dragged task board.
   * @returns {void} Nothing.
   */
  onTaskBoardDragEnd = (taskBoard) => {
    if (this.s.draggingTaskBoardId === taskBoard.id()) {
      this.s.draggingTaskBoardId = null
    }
    this.p.onTaskBoardDragEnd(taskBoard)
  }

  /**
   * Stores a task-card slot height used for live drag spacing.
   * @param {TaskBoard} taskBoard Measured task board.
   * @param {number} slotHeight Measured card height plus vertical gap.
   * @param {BoardItemLayout} layout Task-card layout inside the content view.
   * @returns {void} Nothing.
   */
  onTaskBoardLayout = (taskBoard, slotHeight, layout) => {
    const previousSlotHeight = this.taskBoardSlotHeights.get(taskBoard.id())
    this.taskBoardLayouts.set(taskBoard.id(), layout)
    this.reportTaskBoardLayout(taskBoard)
    if (previousSlotHeight !== slotHeight) {
      this.taskBoardSlotHeights.set(taskBoard.id(), slotHeight)
      this.s.layoutRevision = this.s.layoutRevision + 1
    }
  }

  /**
   * Reports every measured task-card layout to the route.
   * @returns {void} Nothing.
   */
  reportTaskBoardLayouts() {
    for (const taskBoard of this.p.taskBoards) {
      this.reportTaskBoardLayout(taskBoard)
    }
  }

  /**
   * Reports one task-card layout in board-row coordinates.
   * @param {TaskBoard} taskBoard Task board whose layout should be reported.
   * @returns {void} Nothing.
   */
  reportTaskBoardLayout(taskBoard) {
    const taskBoardIndex = this.p.taskBoards.findIndex(
      (candidate) => candidate.id() === taskBoard.id()
    )
    if (taskBoardIndex === -1) {
      return
    }
    const taskBoardLayout = this.taskBoardLayouts.get(taskBoard.id())
    if (!this.columnLayout || !this.contentLayout) {
      return
    }
    const slotHeight = this.slotHeightForTaskBoard(taskBoard)
    this.p.onTaskBoardLayoutChange(taskBoard, {
      height: taskBoardLayout
        ? taskBoardLayout.height
        : slotHeight - PROJECT_BOARD_TASK_CARD_MARGIN_BOTTOM,
      width: taskBoardLayout ? taskBoardLayout.width : this.contentLayout.width,
      x: this.columnLayout.x + this.contentLayout.x + (taskBoardLayout?.x || 0),
      y: this.columnLayout.y +
        this.contentLayout.y +
        this.slotTopForTaskBoards(this.p.taskBoards, taskBoardIndex)
    })
  }

  /**
   * Calculates live spacing offset for a task card.
   * @param {TaskBoard} taskBoard Task board whose preview offset should be read.
   * @returns {number} Vertical preview offset.
   */
  previewOffsetYForTaskBoard(taskBoard) {
    const dragPreview = this.p.dragPreview
    if (!dragPreview || taskBoard.id() === dragPreview.taskBoardId) {
      return 0
    }
    const columnId = this.p.column.id()
    const taskBoards = this.p.taskBoards
    const taskBoardIndex = taskBoards.findIndex(
      (candidate) => candidate.id() === taskBoard.id()
    )
    if (taskBoardIndex === -1) {
      return 0
    }
    const sourceIndex = taskBoards.findIndex(
      (candidate) => candidate.id() === dragPreview.taskBoardId
    )
    const draggedSlotHeight = this.draggedSlotHeight(dragPreview)
    if (
      dragPreview.sourceColumnId === columnId &&
      dragPreview.targetColumnId === columnId
    ) {
      return this.sameColumnPreviewOffsetY({
        draggedSlotHeight,
        sourceIndex,
        taskBoardIndex,
        targetIndex: normalizedPreviewInsertionIndex(
          dragPreview.rowNumber,
          taskBoards.length - 1
        )
      })
    }
    if (dragPreview.sourceColumnId === columnId) {
      return sourceIndex !== -1 && taskBoardIndex > sourceIndex
        ? -draggedSlotHeight
        : 0
    }
    if (dragPreview.targetColumnId === columnId) {
      const targetIndex = normalizedPreviewInsertionIndex(
        dragPreview.rowNumber,
        taskBoards.length
      )
      return taskBoardIndex >= targetIndex ? draggedSlotHeight : 0
    }
    return 0
  }

  /**
   * Stable accessor for the dragged card's release-settle offset. Kept as a
   * bound field (not an eager prop value) so the dragged card receives a
   * referentially stable prop and is not re-rendered mid-gesture as the live
   * drop target moves; the offset is computed only when the card releases.
   * @param {TaskBoard} taskBoard Task board whose settle offset should be read.
   * @returns {number} Vertical settle offset from original slot to target slot.
   */
  settleOffsetYForTaskBoard = (taskBoard) => {
    const dragPreview = this.p.dragPreview
    const taskBoards = this.p.taskBoards
    if (
      !dragPreview ||
      taskBoard.id() !== dragPreview.taskBoardId ||
      dragPreview.sourceColumnId !== this.p.column.id() ||
      dragPreview.targetColumnId !== this.p.column.id()
    ) {
      return 0
    }
    const sourceIndex = taskBoards.findIndex(
      (candidate) => candidate.id() === dragPreview.taskBoardId
    )
    if (sourceIndex === -1) {
      return 0
    }
    const targetTaskBoards = taskBoards.filter(
      (candidate) => candidate.id() !== dragPreview.taskBoardId
    )
    const targetIndex = normalizedPreviewInsertionIndex(
      dragPreview.rowNumber,
      targetTaskBoards.length
    )
    return this.slotTopForTaskBoards(targetTaskBoards, targetIndex) -
      this.slotTopForTaskBoards(taskBoards, sourceIndex)
  }

  /**
   * Calculates a same-column sibling offset.
   * @param {{draggedSlotHeight: number, sourceIndex: number, targetIndex: number, taskBoardIndex: number}} args Preview positions.
   * @returns {number} Vertical preview offset.
   */
  sameColumnPreviewOffsetY({
    draggedSlotHeight,
    sourceIndex,
    targetIndex,
    taskBoardIndex
  }) {
    if (sourceIndex === -1 || targetIndex === sourceIndex) {
      return 0
    }
    if (
      targetIndex > sourceIndex &&
      taskBoardIndex > sourceIndex &&
      taskBoardIndex <= targetIndex
    ) {
      return -draggedSlotHeight
    }
    if (
      targetIndex < sourceIndex &&
      taskBoardIndex >= targetIndex &&
      taskBoardIndex < sourceIndex
    ) {
      return draggedSlotHeight
    }
    return 0
  }

  /**
   * Reads the dragged card slot height.
   * @param {ProjectBoardDragPreview} dragPreview Current drag preview.
   * @returns {number} Slot height used for sibling offsets.
   */
  draggedSlotHeight(dragPreview) {
    if (!dragPreview) {
      return DEFAULT_TASK_BOARD_SLOT_HEIGHT
    }
    return this.taskBoardSlotHeights.get(dragPreview.taskBoardId) ||
      DEFAULT_TASK_BOARD_SLOT_HEIGHT
  }

  /**
   * Sums slot heights before an index.
   * @param {TaskBoard[]} taskBoards Ordered task boards.
   * @param {number} endIndex Exclusive end index.
   * @returns {number} Top offset for the slot.
   */
  slotTopForTaskBoards(taskBoards, endIndex) {
    return taskBoards
      .slice(0, endIndex)
      .reduce(
        (topOffset, taskBoard) =>
          topOffset + this.slotHeightForTaskBoard(taskBoard),
        0
      )
  }

  /**
   * Reads a task-card slot height with a fallback before layout measurement.
   * @param {TaskBoard} taskBoard Task board whose slot height should be read.
   * @returns {number} Measured height plus the card gap.
   */
  slotHeightForTaskBoard(taskBoard) {
    return this.taskBoardSlotHeights.get(taskBoard.id()) ||
      DEFAULT_TASK_BOARD_SLOT_HEIGHT
  }
}

/** @type {NamedExoticComponent<ProjectBoardColumnCardProps>} */
const ProjectBoardColumnCardComponent = memo(shapeComponent(ProjectBoardColumnCard))

export default ProjectBoardColumnCardComponent

/**
 * Bounds a one-based row number to an insertion index.
 * @param {number} rowNumber One-based row number.
 * @param {number} maxIndex Maximum allowed insertion index.
 * @returns {number} Zero-based insertion index.
 */
function normalizedPreviewInsertionIndex(rowNumber, maxIndex) {
  return Math.max(0, Math.min(rowNumber - 1, maxIndex))
}

/**
 * Checks whether two task board arrays contain the same ids in the same order.
 * @param {TaskBoard[]} previousTaskBoards Previous task board order.
 * @param {TaskBoard[]} nextTaskBoards Next task board order.
 * @returns {boolean} Whether the order is unchanged.
 */
function taskBoardIdsMatch(previousTaskBoards, nextTaskBoards) {
  return previousTaskBoards.length === nextTaskBoards.length &&
    previousTaskBoards.every(
      (taskBoard, index) => taskBoard.id() === nextTaskBoards[index].id()
    )
}

/**
 * Reads a React Native layout payload.
 * @param {{nativeEvent?: {layout?: BoardItemLayout}}} event Native event carrying dimensions.
 * @returns {BoardItemLayout | null} Layout value, if complete.
 */
function layoutFromEvent(event) {
  const layout = event.nativeEvent?.layout
  if (
    !layout ||
    typeof layout.height !== "number" ||
    typeof layout.width !== "number" ||
    typeof layout.x !== "number" ||
    typeof layout.y !== "number"
  ) {
    return null
  }
  return layout
}
